import React, { useEffect, useState } from "react";
import { Lock, Sparkles, ExternalLink } from "lucide-react";

/**
 * MembershipGate  (2026-07-09 — rewired to gate on a CUSTOM CLAIM)
 *
 * Wraps the authenticated app surface and decides: render the dashboard,
 * the paywall, or pass through (legacy).
 *
 * WHY THIS CHANGED: the old version read `users/{uid}/settings/membership`
 * from Firestore. Under the live security rules a signed-in user can WRITE
 * their own doc/subcollections, so a user could set `active:true` on
 * themselves from the browser and bypass the paywall. We now read the
 * membership from the Firebase ID token's CUSTOM CLAIMS, which are set only
 * by the billing Worker (Admin) and can never be modified by the client.
 *
 * Claim shape (set by the Worker): { member: boolean, tier: "galaxy"|"apex" }
 *
 * Decision matrix:
 *   claim.member === true    -> children (dashboard)
 *   claim.member === false   -> paywall
 *   no claim yet             -> LEGACY_MODE_PASSTHROUGH
 *   token read fails         -> children (fail-open, log warning)
 */

const LEGACY_MODE_PASSTHROUGH = true; // Phase A — flip to false after the audit/cutoff
const MEMBERSHIP_PURCHASE_URL = "https://arcrypto.io/membership";

/* ─── Membership reader (custom claims) ─────────────────────────── */
async function fetchMembership(user) {
  if (!user || !user.getIdTokenResult) return { state: "missing", tier: null };
  try {
    // force-refresh so a just-provisioned claim is picked up immediately
    const res = await user.getIdTokenResult(true);
    const c = res.claims || {};
    if (c.member === true) return { state: "active", tier: c.tier || null };
    if (c.member === false) return { state: "inactive", tier: c.tier || null };
    return { state: "missing", tier: null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[MembershipGate] claim read failed, failing open:", err.message);
    return { state: "error", tier: null };
  }
}

/* ─── Paywall card (unchanged from prior version) ───────────────── */
const PaywallCard = ({ tierHint }) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-arc-bg">
    <div className="max-w-md w-full surface-xl p-7 sm:p-8 text-center">
      <div className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-arc-gold/15 border border-arc-gold/30 flex items-center justify-center">
        <Lock size={22} className="text-arc-gold" />
      </div>
      <p className="eyebrow text-arc-gold mb-2">Membership required</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-arc-ink mb-3">
        FIRE Tracker is an ARCrypto member benefit
      </h1>
      <p className="text-sm text-arc-muted leading-relaxed mb-6">
        Your account doesn't currently have an active Galaxy or Apex membership.
        Renew or activate your membership to regain access.
      </p>
      <a
        href={MEMBERSHIP_PURCHASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-tactile btn-gold w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold mb-3"
      >
        <Sparkles size={14} />
        View membership options
        <ExternalLink size={12} />
      </a>
      <p className="text-[11px] text-arc-dim mt-4">
        Already a member and seeing this in error? Contact support and we'll get
        your access restored.
      </p>
      {tierHint && (
        <p className="text-[10px] text-arc-dim mt-2 font-mono uppercase tracking-[0.14em]">
          last tier: {tierHint}
        </p>
      )}
    </div>
  </div>
);

/* ─── Gate component ────────────────────────────────────────────── */
const MembershipGate = ({ user, children }) => {
  const [decision, setDecision] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!user || !user.uid) {
      setDecision({ status: "pass" });
      return;
    }
    fetchMembership(user).then(({ state, tier }) => {
      if (cancelled) return;
      if (state === "active") return setDecision({ status: "pass", tier });
      if (state === "inactive") return setDecision({ status: "blocked", tier });
      if (state === "missing")
        return setDecision({ status: LEGACY_MODE_PASSTHROUGH ? "pass" : "blocked", tier });
      // error → fail open
      setDecision({ status: "pass", tier: null });
    });
    return () => { cancelled = true; };
  }, [user]);

  if (decision.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-arc-bg">
        <div className="w-8 h-8 rounded-full border-2 border-arc-gold/30 border-t-arc-gold animate-spin" />
      </div>
    );
  }
  if (decision.status === "blocked") return <PaywallCard tierHint={decision.tier} />;
  return children;
};

export default MembershipGate;
